import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Share2, UserPlus, Trash2, ShieldCheck, Clock, Star, DollarSign, MessageCircle, CalendarClock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import RateCoachDialog from "./RateCoachDialog";
import CoachPaymentDialog from "./CoachPaymentDialog";
import CoachChatPanel from "./CoachChatPanel";
import BookingRequestDialog from "./BookingRequestDialog";

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";

/** Profile card — grant a coach view-only access to your logs by email. */
export default function ShareProgressCard() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [rateTarget, setRateTarget] = useState(null); // { coachId, coachEmail }
  const [payTarget, setPayTarget] = useState(null);   // { coachId, label }
  const [bookTarget, setBookTarget] = useState(null); // { coachId, label }
  const [chatOpen, setChatOpen] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach-shares");
      setShares(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      await api.post("/coach-shares", { coach_email: email.trim().toLowerCase() });
      toast.success("Access granted — your coach can now view your logs.");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (share) => {
    try {
      await api.delete(`/coach-shares/${share.id}`);
      toast.success("Access revoked.");
      setShares((prev) => prev.filter((s) => s.id !== share.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="share-progress-card">
      <div className="flex items-center gap-2 mb-1">
        <Share2 className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Share Progress</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Give your coach view-only access</h3>
      <p className="text-sm text-slate-500 mb-5 leading-relaxed">
        Enter your coach's email. They'll see your live shots, nutrition, weight, and supplements — but can't
        edit anything. Revoke access anytime.
      </p>

      <form onSubmit={invite} className="flex flex-wrap gap-2 mb-5">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="coach@example.com"
          className="flex-1 min-w-[220px] bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
          data-testid="share-progress-email"
        />
        <Button
          type="submit"
          disabled={!email.trim() || inviting}
          className="text-white"
          style={{ background: inviting ? TEAL_D : TEAL }}
          data-testid="share-progress-invite"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> {inviting ? "Sharing…" : "Grant access"}
        </Button>
      </form>

      {!loading && shares.length > 0 && (
        <div className="space-y-3" data-testid="share-progress-list">
          {shares.map((s) => (
            <div key={s.id} data-testid={`share-progress-row-${s.id}`}>
              <div className="border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                     style={{ background: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 100%)", color: "#FFF" }}>
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{s.coach_email}</div>
                  <div className="text-[11px] mt-0.5 flex items-center gap-1.5">
                    {s.coach_is_verified ? (
                      <span className="text-teal-700 font-semibold inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Verified coach · has access
                      </span>
                    ) : (
                      <span className="text-amber-700 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Awaiting admin verification of this coach
                      </span>
                    )}
                  </div>
                </div>
                {s.coach_is_verified && s.coach_user_id && (
                  <>
                    <button
                      type="button"
                      onClick={() => setChatOpen((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
                      className="h-8 px-2.5 rounded-md border border-teal-200 hover:bg-teal-50 flex items-center gap-1.5 text-teal-700 shrink-0"
                      title="Message this coach"
                      data-testid={`share-progress-chat-${s.id}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span className="text-[11px] uppercase tracking-widest font-semibold">
                        {chatOpen[s.id] ? "Close" : "Chat"}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayTarget({ coachId: s.coach_user_id, label: s.coach_name || s.coach_email })}
                      className="h-8 px-2.5 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5 text-slate-700 shrink-0"
                      title="See pricing"
                      data-testid={`share-progress-pricing-${s.id}`}
                    >
                      <DollarSign className="w-3.5 h-3.5" />
                      <span className="text-[11px] uppercase tracking-widest font-semibold">Pricing</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookTarget({ coachId: s.coach_user_id, label: s.coach_name || s.coach_email })}
                      className="h-8 px-2.5 rounded-md border border-teal-200 hover:bg-teal-50 flex items-center gap-1.5 text-teal-700 shrink-0"
                      title="Request a booking slot"
                      data-testid={`share-progress-book-${s.id}`}
                    >
                      <CalendarClock className="w-3.5 h-3.5" />
                      <span className="text-[11px] uppercase tracking-widest font-semibold">Book</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRateTarget({ coachId: s.coach_user_id, coachEmail: s.coach_email, coachName: s.coach_name })}
                      className="h-8 px-2.5 rounded-md border border-amber-200 hover:bg-amber-50 flex items-center gap-1.5 text-amber-700 shrink-0"
                      title="Leave a star rating (unlocks after 30 days)"
                      data-testid={`share-progress-rate-${s.id}`}
                    >
                      <Star className="w-3.5 h-3.5" />
                      <span className="text-[11px] uppercase tracking-widest font-semibold">Rate</span>
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => revoke(s)}
                  className="w-8 h-8 rounded-md border border-rose-200 hover:bg-rose-50 flex items-center justify-center text-rose-600 shrink-0"
                  title="Revoke access"
                  data-testid={`share-progress-revoke-${s.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Inline chat panel when opened */}
              {s.coach_is_verified && chatOpen[s.id] && (
                <div className="mt-2">
                  <CoachChatPanel
                    shareId={s.id}
                    counterpartyLabel={s.coach_name || s.coach_email}
                    defaultOpen
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!loading && shares.length === 0 && (
        <div className="text-xs text-slate-500 italic">No coaches have access yet.</div>
      )}

      <RateCoachDialog
        open={!!rateTarget}
        coachId={rateTarget?.coachId}
        coachEmail={rateTarget?.coachEmail}
        coachName={rateTarget?.coachName}
        onClose={() => setRateTarget(null)}
      />

      <CoachPaymentDialog
        open={!!payTarget}
        coachId={payTarget?.coachId}
        coachLabel={payTarget?.label}
        onClose={() => setPayTarget(null)}
      />

      <BookingRequestDialog
        open={!!bookTarget}
        coachId={bookTarget?.coachId}
        coachLabel={bookTarget?.label}
        onClose={() => setBookTarget(null)}
      />
    </div>
  );
}
